import { parseAppUtcDate } from "@/lib/format-datetime"

export interface WinRateWindow {
  winRate: number | null
  closedCount: number
  wins: number
  losses: number
}

function toNum(v: unknown): number | null {
  const n = typeof v === "number" ? v : Number(v)
  return Number.isFinite(n) ? n : null
}

function closedAtMs(item: Record<string, unknown>): number | null {
  const raw =
    item.closed_at_utc ??
    item.closed_at_local ??
    item.closed_at ??
    item.time
  const d = parseAppUtcDate(raw != null ? String(raw) : null)
  return d ? d.getTime() : null
}

type ClosedRoundTrip = {
  closedAtMs: number
  pnl: number
}

/** Win rate for closed round-trips in the last `days` days (matches dashboard ledger rows). */
export function computeWinRateForDays(
  items: Array<Record<string, unknown>>,
  days: number,
): WinRateWindow {
  const windowMs = Math.max(1, days) * 24 * 60 * 60 * 1000
  const cutoff = Date.now() - windowMs
  const closed: ClosedRoundTrip[] = []

  const dealGroups = new Map<
    string,
    { latestOut: Record<string, unknown> | null; pnlTotal: number }
  >()

  for (const [idx, item] of items.entries()) {
    const rowType = String(item.row_type ?? "").toLowerCase()

    if (rowType === "mt5_deal") {
      const positionId = toNum(item.position_id)
      const dealTicket = toNum(item.deal_ticket)
      const key =
        positionId != null && positionId > 0
          ? `pos:${positionId}`
          : `deal:${dealTicket ?? idx}`
      const leg = String(item.entry_leg ?? "").toLowerCase()
      const group = dealGroups.get(key) ?? { latestOut: null, pnlTotal: 0 }
      if (leg === "out") {
        const nowMs = closedAtMs(item)
        const prevMs = group.latestOut ? closedAtMs(group.latestOut) : null
        if (!group.latestOut || (nowMs != null && (prevMs == null || nowMs >= prevMs))) {
          group.latestOut = item
        }
      }
      group.pnlTotal += toNum(item.pnl_leg) ?? 0
      dealGroups.set(key, group)
      continue
    }

    if (rowType === "open_position") continue

    const status = String(item.status ?? "").toLowerCase()
    if (status !== "closed") continue

    const when = closedAtMs(item)
    if (when == null) continue
    closed.push({
      closedAtMs: when,
      pnl: toNum(item.realized_pnl) ?? 0,
    })
  }

  for (const group of dealGroups.values()) {
    if (!group.latestOut) continue
    const when = closedAtMs(group.latestOut)
    if (when == null) continue
    closed.push({
      closedAtMs: when,
      pnl: group.pnlTotal,
    })
  }

  const inWindow = closed.filter((t) => t.closedAtMs >= cutoff)
  const wins = inWindow.filter((t) => t.pnl > 0).length
  const losses = inWindow.filter((t) => t.pnl < 0).length
  const closedCount = inWindow.length

  return {
    winRate: closedCount > 0 ? wins / closedCount : null,
    closedCount,
    wins,
    losses,
  }
}
